import React, { FC } from 'react';

import { GetStaticPathsResult, GetStaticProps, GetStaticPropsResult } from 'next';
import { ICanonical } from '../interfaces/canonical.interface';
import styled from '@emotion/styled';
import { defaultTranslationNamespaces } from '../constants/translations';
import { serverSideNavigation } from '../lib/strapi/server-side-navigation';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ssrStandard } from '../@types/codegen/page';
import { PageTitle } from '../components/content/page-title';
import { ThemeProvider } from '@emotion/react';
import { ContentContainerMax } from '../layout/container';
import { i18nextConfig } from '../next-i18next.config';
import { Languages } from '../types/languages.type';
import { i18n, useTranslation } from 'next-i18next';
import { ssrGeneratePageUrl } from '../lib/strapi/ssr-generate-page-url';
import { ImageHeader, ImageHeaderProps } from '../components/header/image-header';
import { Zones } from '../zones/zones';
import { IStrapiSeo, StrapiSeo } from '../seo/strapi-seo';
import { FaqPageSchema } from '../schema.org/faq-page.schema';
import { getZoneApiCache } from '../zones/zone-cache';
import { IDigandoBasicProps } from '../interfaces/basic-props.interface';
import { PublicationState, StandardPageResponseFragment, AllZonesFragment } from '../@types/codegen/graphql';
import { StrapiImageFormats } from '../@types/strapi';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { onlineStoreSchema } from '../schema.org/online-store.schema';
import { websiteSchema } from '../schema.org/website.schema';
import { isFeatureFlagEnabled } from '../lib/environment';

interface IPageProps extends IDigandoBasicProps, IStrapiSeo {
  pageTitle: string;
  header: ImageHeaderProps;
  content: AllZonesFragment[];
}

const urlsToRenderSchema = ['ueber-uns', 'kontakt'];

const Uid: FC<IPageProps> = (props) => {
  const router = useRouter();
  const [t] = useTranslation('schema-org');

  return (
    <>
      {(isFeatureFlagEnabled('isSchemaMarkupEnabled') && urlsToRenderSchema.includes(router.query.uid as string ?? '')) && (
        <Head>
          <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(onlineStoreSchema({
            name: t('onlineStore.name'),
            slogan: t('onlineStore.slogan'),
            taxID: t('onlineStore.taxID'),
            contactPoint: {
              telephone: t('onlineStore.contactPoint.telephone'),
              email: t('onlineStore.contactPoint.email'),
              contactType: t('onlineStore.contactPoint.contactType'),
            },
            address : {
              addressLocality: t('onlineStore.address.addressLocality'),
              addressRegion: t('onlineStore.address.addressRegion'),
              postalCode: t('onlineStore.address.postalCode'),
              streetAddress: t('onlineStore.address.streetAddress'),
              addressCountry: t('onlineStore.address.addressCountry'),
            },
            foundingDate: t('onlineStore.foundingDate'),
            iso6523Code: t('onlineStore.iso6523Code')
          })) }} />
          <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema()) }} />
        </Head>
      )}

      <StrapiSeo seo={props.seo} pageTitle={props.pageTitle} schemaOrg={{ enable: true }} />

      <ImgWrapper>
        <ImageHeader
          {...props.header}
          pageTitle={props.pageTitle}
          breadcrumbItems={props.header.breadcrumbItems}
        />
      </ImgWrapper>
      <ThemeProvider
        theme={{
          contentType: 'content',
        }}
      >
        <ContentContainerMax>
          <PageTitle title={props.pageTitle} />
        </ContentContainerMax>
        <Zones zones={props.content} />
      </ThemeProvider>

      <FaqPageSchema zones={props.content} />
    </>
  );
};

export const getStaticProps: GetStaticProps<IPageProps> = async ({
  params,
  locale,
  draftMode,
}): Promise<GetStaticPropsResult<IPageProps>> => {
  try {
    const standardPageReq = ssrStandard.getServerPage({
      variables: {
        slug: params?.uid as string,
        publicationState: draftMode ? PublicationState.Preview : PublicationState.Live,
      },
    },
    {},
    );
    const [pageRes, ssrNavigation] = await Promise.all([standardPageReq, serverSideNavigation()]);

    const standardPage: StandardPageResponseFragment | null = pageRes?.props?.data.standardPages?.data?.[0] ?? null;

    if (null === standardPage || !standardPage?.attributes) {
      return {
        notFound: true,
        revalidate: 10,
      };
    }

    const canonicals: ICanonical[] = standardPage.attributes.localizations?.data.map(page => {
      return {
        lang: page.attributes?.locale as Languages ?? 'en',
        url: ssrGeneratePageUrl(standardPage?.__typename ?? '', page.attributes?.slug ?? '', pageRes.props.data?.categories?.data ?? []),
        alternate: true,
      };
    }) ?? [];

    canonicals.push({
      url: ssrGeneratePageUrl(standardPage?.__typename ?? '', standardPage.attributes.slug, pageRes.props.data?.categories?.data ?? []),
      lang: standardPage.attributes.locale as Languages ?? 'de',
      alternate: false,
    });

    const cache = await getZoneApiCache(standardPage?.attributes?.content as AllZonesFragment[] ?? []);
    const seoImageFormats: StrapiImageFormats | null = standardPage.attributes.seo?.image?.data?.attributes?.formats ?? null;

    return {
      revalidate: 15 * 60, // Time in seconds to renew cache.
      props: {
        apolloState: {
          ...cache?.apolloState ?? {},
          ...pageRes?.props.apolloState ?? {},
          ...ssrNavigation.apolloState,
          ROOT_QUERY: {
            ...pageRes?.props.apolloState?.ROOT_QUERY ?? {},
            ...cache?.apolloState?.ROOT_QUERY ?? {},
            ...ssrNavigation.apolloState?.ROOT_QUERY ?? {},
          }
        },
        data: {},
        ...(await serverSideTranslations(locale ?? 'de', [...defaultTranslationNamespaces], i18nextConfig)),
        pageTitle: standardPage.attributes.title,
        header: {
          image: standardPage.attributes.header?.image?.data?.attributes ?? null,
          pageTitle: standardPage.attributes.title,
          breadcrumbItems: [
            { href: '/', name: i18n?.t('common:navigation.breadcrumb.homepage') ?? '' },
          ],
        },
        seo: {
          title: standardPage.attributes.seo?.title ?? standardPage.attributes.title,
          description: standardPage.attributes.seo?.description ?? '',
          url: ssrGeneratePageUrl(standardPage.__typename ?? '', standardPage.attributes.slug ?? '/', pageRes.props.data?.categories?.data ?? []),
          image: seoImageFormats?.['1200x630'] ?? null,
          canonicals,
          datePublished: standardPage.attributes.publishedAt,
          dateModified: standardPage.attributes.updatedAt,
        },
        content: standardPage?.attributes?.content as AllZonesFragment[] ?? [],
      },
    };
  } catch (error) {
    return {
      notFound: true,
      revalidate: 10,
    };
  }
};

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

const ImgWrapper = styled.div`
  overflow: hidden;
`;

export default Uid;
